import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useScrollSpy, type ScrollSpySection } from '../../hooks/useScrollSpy';

interface SectionScrollNavProps {
  sections: ScrollSpySection[];
  className?: string;
}

type TooltipPos = { top: number; right: number };

const TOOLTIP_GAP = 10;

const SectionScrollNav: React.FC<SectionScrollNavProps> = ({
  sections,
  className = '',
}) => {
  const { activeId, scrollToSection } = useScrollSpy(sections);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState<TooltipPos | null>(null);
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const showTooltip = useCallback((id: string) => {
    const btn = buttonRefs.current[id];
    if (!btn) return;

    const rect = btn.getBoundingClientRect();
    setTooltipPos({
      top: rect.top + rect.height / 2,
      right: window.innerWidth - rect.left + TOOLTIP_GAP,
    });
    setHoveredId(id);
  }, []);

  const hideTooltip = useCallback(() => {
    setHoveredId(null);
    setTooltipPos(null);
  }, []);

  if (sections.length === 0) return null;

  const hoveredLabel = sections.find((s) => s.id === hoveredId)?.label;

  return (
    <>
      <nav
        className={`fixed right-7 top-1/2 z-40 hidden -translate-y-1/2 md:right-8 xl:block ${className}`}
        aria-label="Navigation des sections"
      >
        <div className="glass-liquid glass-nav relative z-[1] px-2 py-3">
          <div className="relative flex flex-col items-center">
            {sections.map((section, index) => {
              const isActive = activeId === section.id;

              return (
                <React.Fragment key={section.id}>
                  {index > 0 ? (
                    <div
                      className="h-4 w-px shrink-0 bg-neutral-300/80"
                      aria-hidden
                    />
                  ) : null}

                  <button
                    ref={(node) => {
                      buttonRefs.current[section.id] = node;
                    }}
                    type="button"
                    onClick={() => scrollToSection(section.id)}
                    onMouseEnter={() => showTooltip(section.id)}
                    onMouseLeave={hideTooltip}
                    onFocus={() => showTooltip(section.id)}
                    onBlur={hideTooltip}
                    className="flex h-6 w-6 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 focus-visible:ring-offset-2"
                    aria-label={`Aller à ${section.label}`}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    <span
                      className={`block rounded-full transition-all duration-300 ease-apple ${
                        isActive
                          ? 'h-2.5 w-2.5 bg-neutral-800 ring-[3px] ring-orange-500/35 ring-offset-1 ring-offset-white/80'
                          : 'h-2.5 w-2.5 border-2 border-neutral-400/80 bg-white/90 ring-2 ring-white/60 hover:border-orange-400/60 hover:bg-white'
                      }`}
                    />
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </nav>

      {hoveredId && tooltipPos && hoveredLabel
        ? createPortal(
            <div
              role="tooltip"
              className="pointer-events-none fixed z-[9999] whitespace-nowrap rounded-lg bg-neutral-900 px-3 py-2 text-[13px] font-medium tracking-wide text-white shadow-[0_8px_24px_rgba(0,0,0,0.22)]"
              style={{
                top: tooltipPos.top,
                right: tooltipPos.right,
                transform: 'translateY(-50%)',
              }}
            >
              {hoveredLabel}
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

export default SectionScrollNav;
